// Port of TrailMap/TrailMapProvider.swift (TrailBivvyKit): map attributions, map styles and the provider interface.
//
// Web deviations:
// - `MapStyle.usesAppleBaseMap` becomes `usesFallbackBaseMap`: the web has no Apple map, the fallback base map is
//   OpenStreetMap's standard tiles (OpenStreetMapProvider), drawn below the style's layers.
// - `downloadableRegions` is synchronous (pure computation; Swift checks task cancellation between regions).
// - `attributionsFor(layers)` is part of the interface so the map can credit every provider whose tiles are visible
//   when several providers are drawn side by side (CompositeMapProvider.mosaic).

import type { GeoBounds, OfflinePackageLevel, OfflineRegion, ProtectedArea, TrailRoute } from '../core/models';
import type { TileLayerDescriptor } from '../offline/TileLayerDescriptor';
import type { MapLayerPreferences } from './MapLayer';

/** A licence attribution to display on the map (and on the Sources & Licences screen). */
export interface MapAttribution {
  /** Text as required by the licensor, e.g. "© swisstopo". */
  text: string;
  /** Licensor page opened when the attribution is tapped. */
  url?: string;
}

export const MapAttributions = {
  /** Federal Office of Topography swisstopo (mandatory whenever swisstopo data is displayed). */
  swisstopo: { text: '© swisstopo', url: 'https://www.swisstopo.admin.ch' } as MapAttribution,
  /** Federal Office for the Environment (wildlife rest zones, hunting ban areas). */
  bafu: { text: '© BAFU/FOEN', url: 'https://www.bafu.admin.ch' } as MapAttribution,
  /** OpenStreetMap (the web fallback base map, and OSM data inside TopPlusOpen). */
  openStreetMap: { text: '© OpenStreetMap contributors', url: 'https://www.openstreetmap.org/copyright' } as MapAttribution,
};

export function sameAttribution(a: MapAttribution, b: MapAttribution): boolean {
  return a.text === b.text && a.url === b.url;
}

/** What to draw for a region with given preferences. */
export interface MapStyle {
  /** Raster base map, or undefined when only the fallback base map is shown. */
  baseLayer?: TileLayerDescriptor;
  /** Raster overlays, bottom to top. */
  rasterOverlays: TileLayerDescriptor[];
  /** `true` when the fallback base map (OpenStreetMap) is drawn under the style (Swift `usesAppleBaseMap`). */
  usesFallbackBaseMap: boolean;
  /** Attributions that must be visible on the map. */
  attributions: MapAttribution[];
}

/** A source of base maps, overlays, offline tile layers and protected areas for some part of the world. */
export interface TrailMapProvider {
  /** Stable identifier. */
  readonly id: string;
  /** Provider name (data, not a sentence). */
  readonly displayName: string;
  /** Area where the provider's maps are meaningful. */
  readonly coverage: GeoBounds;
  /** Layers to draw for `bounds`. */
  mapStyle(bounds: GeoBounds, preferences: MapLayerPreferences): MapStyle;
  /** Raster layers stored in offline packages of `level` (empty when the terms forbid caching). */
  offlineTileLayers(level: OfflinePackageLevel): TileLayerDescriptor[];
  /** Offline regions covering the route: one per ≤ 40 km, following stage boundaries when possible. */
  downloadableRegions(route: TrailRoute, level: OfflinePackageLevel, now: number): OfflineRegion[];
  /** Protected areas intersecting `bounds` (local data, optionally merged with official online data). Never rejects
   *  for network errors. */
  protectedAreas(bounds: GeoBounds): Promise<ProtectedArea[]>;
  /** Credits for the given displayed layers of this provider (web addition, see the module comment). */
  attributionsFor(layers: readonly TileLayerDescriptor[]): MapAttribution[];
}

/** Source of protected areas from the bundled/offline datasets for given bounds. */
export type LocalProtectedAreas = (bounds: GeoBounds) => Promise<ProtectedArea[]>;

export const noLocalProtectedAreas: LocalProtectedAreas = async () => [];
